import React from 'react'

import { Avatar, Box, SearchField, Text } from '@factorialco/deprecated-design-system'
import _ from 'lodash'

import SingleChoice from 'modules/core/components/Filter/filters/SingleChoice'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

interface ManagerAccess {
  avatar: string
}

type InputEvent = React.ChangeEvent<HTMLInputElement>

export interface Manager {
  fullName: string
  id: string
  access: ManagerAccess
}

interface Props {
  value: string
  onChange: (value: string | undefined) => void
  managers: Manager[]
}

const ManagerSelector = ({ value, onChange, managers }: Props) => {
  const [search, setSearch] = React.useState<string>()
  const [filteredManagers, setFilteredManagers] = React.useState<Manager[]>()

  const filterManagers = _.debounce((searchTerm: string) => {
    const res = managers.filter((e) => e.fullName.toUpperCase().includes(searchTerm.toUpperCase()))
    setFilteredManagers(res)
  }, 500)

  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm)
    filterManagers(searchTerm)
  }

  const reset = () => {
    setFilteredManagers(managers)
    setSearch('')
  }

  const handleValueChanged = (value: string | undefined) => {
    reset()
    onChange(value)
  }

  const getManagerComponent = (m: Manager) => (
    <Box flexDirection="row" gap="s8">
      <Box flexNoShrink>
        <Avatar size="s24" url={buildUrl(m.access?.avatar || '')} label={m.fullName} />
      </Box>
      <Text.Normal weight="medium" ellipsis color="grey800">
        {m.fullName}
      </Text.Normal>
    </Box>
  )

  const selectedOption = useUnsafeMemo(() => {
    if (value) {
      const m = managers.find((m) => m.id.toString() === value)
      if (m) {
        return {
          label: m.fullName,
          value: m.id.toString(),
          component: getManagerComponent(m),
        }
      }
    }
  }, [managers, value])

  const options = useUnsafeMemo(
    () =>
      (filteredManagers || managers).map((m) => ({
        label: m.fullName,
        value: m.id.toString(),
        component: getManagerComponent(m),
      })),
    [filteredManagers, managers]
  )

  return (
    <SingleChoice
      header={
        <Box paddingY="s8" paddingX="s12">
          <SearchField
            variant="big"
            search={search || ''}
            onSearch={(e: InputEvent) => handleSearch(e.target.value)}
            placeholder={i18n.t('common.search')}
          />
        </Box>
      }
      label="Manager"
      selectedOption={selectedOption}
      onValueChange={handleValueChanged}
      options={options}
    />
  )
}

export default ManagerSelector
